import React, { useState, useEffect, useRef } from "react";
import {
  CalendarWrapper,
  DatePickerWrapper,
  ScrollContainer,
  ScrollItem,
  SelectedHighlight,
} from "./CustomDatePicker.styles";
import { VerticalSpacer } from "../../primitives/Spacer/VerticalSpacer";
import { Button } from "../Button/Button";
import { CustomDatePickerProps } from "./CustomDatePicker.types";
import { format, getDaysInMonth, addYears, subYears } from "date-fns";
import { Body3RegularTypography } from "../../primitives/Typography/typographyVariants";
import FlexRow from "../Layout/FlexRow/FlexRow";

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  onSelect,
  onClose,
  initialDate,
  minDate = null,
  maxDate,
}) => {
  const today = new Date();
  const startDate = initialDate || today;
  const ITEM_HEIGHT = 53;
  const VISIBLE_ITEMS = 3;

  const [selectedDay, setSelectedDay] = useState(startDate.getDate());
  const [selectedMonth, setSelectedMonth] = useState(startDate.getMonth());
  const [selectedYear, setSelectedYear] = useState(startDate.getFullYear());

  const dayContainerRef = useRef<HTMLDivElement>(null);
  const monthContainerRef = useRef<HTMLDivElement>(null);
  const yearContainerRef = useRef<HTMLDivElement>(null);
  const scrollingRef = useRef<boolean>(false);
  const safeModulo = (n: number, m: number) => ((n % m) + m) % m;
  const generateExtendedArray = (array: any[]) => [
    ...array,
    ...array,
    ...array,
  ];

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const generateYearsArray = () => {
    const currentYear = new Date().getFullYear();
    const startYear = minDate ? minDate.getFullYear() : currentYear - 99;
    const endYear = maxDate ? maxDate.getFullYear() : currentYear;
    return Array.from(
      { length: endYear - startYear + 1 },
      (_, i) => startYear + i
    );
  };

  const years = generateYearsArray();
  const extendedMonths = generateExtendedArray(months);
  const extendedYears = generateExtendedArray(years);

  const generateDaysArray = (month: number, year: number) =>
    Array.from(
      { length: getDaysInMonth(new Date(year, month)) },
      (_, i) => i + 1
    );

  const [days, setDays] = useState(
    generateDaysArray(selectedMonth, selectedYear)
  );
  const [extendedDays, setExtendedDays] = useState(generateExtendedArray(days));

  const updateSelection = (
    container: HTMLDivElement,
    items: any[],
    setSelected: (value: any) => void
  ) => {
    const scrollTop = container.scrollTop;
    const itemHeight = ITEM_HEIGHT;
    const offset = itemHeight * Math.floor(VISIBLE_ITEMS / 2);

    const selectedIndex = Math.round((scrollTop + offset) / itemHeight);
    const safeIndex = safeModulo(selectedIndex, items.length);
    const selectedItem = items[safeIndex];

    if (typeof selectedItem === "string") {
      setSelected(items.indexOf(selectedItem));
    } else {
      setSelected(selectedItem);
    }
  };

  // Fixed scroll end detection
  const handleScrollEnd = (
    container: HTMLDivElement,
    items: any[],
    setSelected: (value: any) => void
  ) => {
    const scrollTop = container.scrollTop;
    const oneThirdHeight = items.length * ITEM_HEIGHT;

    // Keep scroll within middle third
    if (scrollTop < oneThirdHeight) {
      container.scrollTop += oneThirdHeight;
    } else if (scrollTop > oneThirdHeight * 2) {
      container.scrollTop -= oneThirdHeight;
    }

    // Calculate the closest item position
    const itemHeight = ITEM_HEIGHT;
    const selectedIndex = Math.round(scrollTop / itemHeight) % items.length;
    const targetPosition = selectedIndex * itemHeight;

    // Snap to the closest item
    const snapOffset = oneThirdHeight % itemHeight;
    const snapPosition = targetPosition + snapOffset + oneThirdHeight;

    container.scrollTo({
      top: snapPosition,
      behavior: "smooth",
    });

    updateSelection(container, items, setSelected);
    scrollingRef.current = false;
  };

  const handleScroll = (
    event: React.UIEvent<HTMLDivElement>,
    items: any[],
    setSelected: (value: any) => void
  ) => {
    const container = event.currentTarget;
    scrollingRef.current = true;
    clearTimeout((container as any).dataset?.scrollTimeout);
    (container as any).dataset.scrollTimeout = setTimeout(() => {
      if (scrollingRef.current) {
        handleScrollEnd(container, items, setSelected);
      }
    }, 150) as any;
    const scrollTop = container.scrollTop;
    const oneThirdHeight = items.length * ITEM_HEIGHT;
    if (scrollTop < oneThirdHeight / 2) {
      container.scrollTop += oneThirdHeight;
    } else if (scrollTop > oneThirdHeight * 2.5) {
      container.scrollTop -= oneThirdHeight;
    }
  };

  const handleYearScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const container = event.currentTarget;
    scrollingRef.current = true;

    clearTimeout(container.dataset.scrollTimeout as any);
    container.dataset.scrollTimeout = setTimeout(() => {
      updateSelection(container, years, setSelectedYear);
      scrollingRef.current = false;
    }, 200) as any;
  };

  useEffect(() => {
    const newDays = generateDaysArray(selectedMonth, selectedYear);
    setDays(newDays);
    setExtendedDays(generateExtendedArray(newDays));

    if (selectedDay > newDays.length) {
      setSelectedDay(newDays.length);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    const scrollToMiddle = (
      container: HTMLDivElement,
      index: number,
      items: any[]
    ) => {
      const oneThirdHeight = items.length * ITEM_HEIGHT;
      const centerOffset = (container.offsetHeight - ITEM_HEIGHT) / 2;
      const initialOffset = oneThirdHeight + index * ITEM_HEIGHT - centerOffset;
      container.scrollTop = initialOffset;
    };

    requestAnimationFrame(() => {
      if (dayContainerRef.current) {
        scrollToMiddle(dayContainerRef.current, selectedDay - 1, days);
      }
      if (monthContainerRef.current) {
        scrollToMiddle(monthContainerRef.current, selectedMonth, months);
      }
      if (yearContainerRef.current) {
        const yearIndex = years.indexOf(selectedYear);
        scrollToMiddle(yearContainerRef.current, yearIndex, years);
      }
    });
  }, [days]);

  const handleDateSelect = () => {
    const daysInMonth = getDaysInMonth(new Date(selectedYear, selectedMonth));
    const safeDay = Math.min(selectedDay, daysInMonth);
    const formattedDate = `${safeDay.toString().padStart(2, "0")} ${
      months[selectedMonth]
    } ${selectedYear}`; // Example: '26 Jun (06) 2025'
    onSelect(formattedDate); // :white_tick: Pass string instead of Date
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <CalendarWrapper>
      <DatePickerWrapper>
        <ScrollContainer
          ref={dayContainerRef}
          onScroll={(e) => handleScroll(e, days, setSelectedDay)}
        >
          <SelectedHighlight />
          {extendedDays.map((day, index) => (
            <ScrollItem key={`${day}-${index}`} selected={day === selectedDay}>
              <Body3RegularTypography fontFamily="Lexend">
                {day.toString().padStart(2, "0")}
              </Body3RegularTypography>
            </ScrollItem>
          ))}
        </ScrollContainer>

        <ScrollContainer
          ref={monthContainerRef}
          onScroll={(e) =>
            handleScroll(e, months, (index) => setSelectedMonth(index % 12))
          }
        >
          <SelectedHighlight />
          {extendedMonths.map((month, index) => (
            <ScrollItem
              key={`${month}-${index}`}
              selected={month === months[selectedMonth]}
            >
              <Body3RegularTypography fontFamily="Lexend">
                {month}
              </Body3RegularTypography>
            </ScrollItem>
          ))}
        </ScrollContainer>

        <ScrollContainer ref={yearContainerRef} onScroll={handleYearScroll}>
          <SelectedHighlight />
          {extendedYears.map((year, index) => (
            <ScrollItem
              key={`${year}-${index}`}
              selected={year === selectedYear}
            >
              <Body3RegularTypography fontFamily="Lexend">
                {year}
              </Body3RegularTypography>
            </ScrollItem>
          ))}
        </ScrollContainer>
      </DatePickerWrapper>
      <VerticalSpacer size="large" />
      <FlexRow gap={10}>
        <Button variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleDateSelect}>
          Confirm
        </Button>
      </FlexRow>
      <VerticalSpacer size="large" />
    </CalendarWrapper>
  );
};

export default CustomDatePicker;
